import { FC, useState } from "react";
import { Typography } from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { useFactoryCodCreditInfo, useFactoryCreditBalance } from "hooks/useFactoryCredit";
import { numberFormatInt } from "utils/nuberFormat";
import { getMainCreditLineDisplay, resolveCreditDebtSnapshot } from "utils/creditDisplay";
import ExpiringCreditModal from "components/ManageCredit/ExpiringCreditModal";

const { Text } = Typography;

const CardWrapper = styled.div`
  width: 100%;
  padding: 8px 16px;
  background: #fff;
  /* เดิมมี border: 1px solid #f0f0f0 เหมือน ParcelCreditCard — Requirement: ไม่ต้องตีเส้น */
  border: none;
  border-radius: 8px;
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`;

/**
 * เครดิต COD บนหน้าคำสั่งซื้อ — แสดงครบ 3 บรรทัดตลอดเวลา (ไม่ต้อง hover)
 * Requirement (2026-08-13): อยู่ด้านบนของหน้า + รูปแบบเหมือน ParcelCreditCard (เต็มความกว้าง ชิดขวา ฟอนต์ 20/14)
 * และไม่ให้มีช่องว่างด้านล่างมากเกินไป (margin-bottom 0 / padding กระชับ)
 *
 * เดิม: ลอยมุมขวาบนของ HeaderTabs — มีพื้นที่ว่างใต้การ์ดเพราะแถบแท็บอยู่คนละแถว
 * เดิม (รอบถัดมา): ย้ายลงไปใน OrderTabsCard เหนือตาราง — ผู้ใช้ต้องการให้อยู่ด้านบนของหน้า
 *
 * remaining_quota (ไม่ใช่ usable_credit เฉยๆ) เพราะ usable_credit เป็นเพดานรวมทั้งหมด (ไม่ลดตาม
 * ออเดอร์ที่จองไปแล้ว) ถ้าโชว์เพดานรวมตรงๆ จะดูขัดกับตารางที่บางออเดอร์ติด "เครดิตไม่พอ" ทั้งที่
 * ตัวเลขนี้ยังดูเหลือเยอะ — ต้องโชว์ "ที่เหลือใช้ได้จริงตอนนี้" ถึงจะตรงกับความรู้สึกของตาราง
 *
 * ไม่มีทศนิยม (numberFormatInt) เพราะเป็นตัวเลขที่คูณ multiplier มาแล้ว ไม่จำเป็นต้องละเอียดถึงสตางค์
 */
const OrderCodCreditCard: FC = () => {
  const { data: codInfo } = useFactoryCodCreditInfo();
  const { data: balance } = useFactoryCreditBalance();
  const [expiringModalOpen, setExpiringModalOpen] = useState(false);
  if (!codInfo) return null;

  const remainingQuota = Number(codInfo.remaining_quota ?? 0);
  const multiplier = codInfo.cod_credit_multiplier ?? 2;

  const debtSnap = resolveCreditDebtSnapshot({
    availableCredit: codInfo.available_credit,
    availableCreditAlt: balance?.available_credit,
    hasCreditDebt: codInfo.has_credit_debt,
    hasCreditDebtAlt: balance?.has_credit_debt,
    debtAmount: codInfo.debt_amount,
    debtAmountAlt: balance?.debt_amount,
    debtDisplay: codInfo.debt_display,
    debtDisplayAlt: balance?.debt_display,
    multiplier,
  });

  const mainCredit = getMainCreditLineDisplay({
    availableRaw: debtSnap.availableRaw,
    multiplier,
    remainingQuota,
    debtDisplay: debtSnap.debtDisplay,
    debtAmount: debtSnap.debtAmount,
    hasCreditDebt: debtSnap.hasCreditDebt,
    formatInt: (n) => String(numberFormatInt(n)),
  });

  return (
    <CardWrapper>
      <Text strong style={{ fontSize: 20, lineHeight: 1.3 }}>
        เครดิตที่ใช้ได้ (COD)
      </Text>
      <Text strong style={{ fontSize: 26, lineHeight: 1.3 }}>
        <span style={{ color: mainCredit.color }}>{mainCredit.valueText}</span>
        <span style={{ color: "#1890ff", fontSize: 20 }}> Credit(s)</span>
      </Text>
      {/* ทั้งบรรทัดคลิกเปิด modal ได้ ไม่ใช่แค่ไอคอน — รายละเอียดเต็ม (ยอดรวม + breakdown ตามวันหมดอายุ)
       * อยู่ใน ExpiringCreditModal แล้ว ไม่ต้องสรุปตัวเลข "กำลังหมดอายุ"/"วันหมดอายุ" ซ้ำไว้ตรงนี้อีก —
       * ตัวเล็กกว่า 2 บรรทัดบน (2026-08-26) เพราะเป็นแค่ลิงก์รอง ไม่ใช่ข้อมูลหลัก */}
      <Text
        style={{ fontSize: 12, color: "#1890ff", marginTop: 4, cursor: "pointer" }}
        onClick={() => setExpiringModalOpen(true)}
      >
        รายละเอียด
        <InfoCircleOutlined style={{ marginLeft: 4, fontSize: 11 }} />
      </Text>
      <ExpiringCreditModal open={expiringModalOpen} onClose={() => setExpiringModalOpen(false)} />
    </CardWrapper>
  );
};

export default OrderCodCreditCard;
